// Tagihan Vendor — daftar (#4b-3a, layar 1)
//
// NOL KONSTANTA BISNIS DI FILE INI. Daftar status, wewenang role, ambang
// alasan, dan batas persetujuan dibaca dari GET /purchasing/meta/invoice.
// Yang ada di sini hanya presentasi.
//
// Alur yang tampil di layar ini (gerbang approval Q48):
//   ADMIN_PURCHASING mengajukan → PENDING_APPROVAL
//   KC/Owner menyetujui        → UNPAID (antrean Finance)
//   Finance membayar           → PARTIAL_PAID → PAID
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge, toneFromMeta } from "../../components/etams/StatusBadge";
import { ReceiptText, RefreshCw, ArrowRight, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  listInvoices, getInvoiceMeta, apiErrorMessage,
} from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariVendor, ambilVendor, labelVendor } from "../../lib/sumberPilihan";
import {
  BELUM_SELESAI, paramStatus, saringanStatus, saringanDari, saringanSampai,
} from "../../lib/saringanPurchasing";

//: Cermin `URUT_SAH_INVOICE` di backend/services/vendor_invoice_service.py.
const URUT_SAH = ["invoice_date", "internal_number", "invoice_number", "due_date",
  "total_amount", "status", "created_at"];

//: Tone dikirim backend per status; terjemahan tone → warna hidup di satu
//: tempat (components/etams/StatusBadge, Q68). Pola sama dengan
//: PurchaseOrders.statusBadge — status baru di backend ikut sendirinya.
export function invoiceStatusBadge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

//: Sisa tagihan — bentuk CADANGAN. Sukunya BUKAN karangan UI: persis
//: /meta/payment.remaining_formula = "total_amount - paid_amount -
//: dp_applied_amount". Suku dp_applied WAJIB ikut: tanpa itu sisa terhitung
//: terlalu besar dan nominal yang dikirim Finance pasti kena
//: OVERPAYMENT_BLOCKED.
//:
//: Dipakai apa adanya di layar daftar (yang tidak memuat /meta/payment) dan
//: sebagai jaring pengaman `sisaTagihanMeta` di bawah.
export function sisaTagihan(inv) {
  return (Number(inv?.total_amount) || 0)
       - (Number(inv?.paid_amount) || 0)
       - (Number(inv?.dp_applied_amount) || 0)
       - (Number(inv?.potongan_retur_idr) || 0)
       - (Number(inv?.potongan_uang_lebih_idr) || 0);   // K215 / BUSINESS-90
}

//: Menjalankan rumus sisa APA ADANYA dari /meta/payment.remaining_formula
//: (#4b-3b). Yang dijalankan hanya penjumlahan/pengurangan nama kolom —
//: tidak ada angka, tanda kurung, atau operator lain yang diterima, dan
//: tidak ada eval.
//:
//: Kenapa repot begini alih-alih menyalin tiga suku ke JS: begitu backend
//: menambah suku (mis. nota kredit), layar yang menyalin rumus akan diam-diam
//: menghitung sisa terlalu besar, dan Finance kena OVERPAYMENT_BLOCKED tanpa
//: tahu sebabnya. Angkanya benar bukan karena UI pintar, tapi karena UI tidak
//: pernah memutuskan sendiri sukunya apa saja.
export function jalankanRumus(rumus, baris) {
  if (typeof rumus !== "string" || !rumus.trim() || !baris) return null;
  const bagian = rumus.split(/\s*([+-])\s*/).map(s => s.trim()).filter(Boolean);
  if (bagian.length === 0 || bagian.length % 2 === 0) return null;
  let hasil = 0;
  let tanda = 1;
  for (let i = 0; i < bagian.length; i++) {
    if (i % 2 === 1) {                       // posisi ganjil = operator
      tanda = bagian[i] === "-" ? -1 : 1;
      continue;
    }
    const kolom = bagian[i];
    if (!/^[a-z_][a-z0-9_]*$/i.test(kolom)) return null;
    const n = Number(baris[kolom]);
    if (!Number.isFinite(n)) return null;    // kolom tak ada / bukan angka
    hasil += tanda * n;
  }
  return hasil;
}

//: Sisa tagihan menurut backend, dengan `sisaTagihan` sebagai cadangan kalau
//: meta belum termuat atau rumusnya memuat kolom yang tidak ikut terkirim.
export function sisaTagihanMeta(inv, paymentMeta) {
  const r = jalankanRumus(paymentMeta?.remaining_formula, inv);
  return r === null ? sisaTagihan(inv) : r;
}

export default function VendorInvoices() {
  const navigate = useNavigate();
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  //: Vendor terpilih (objek) — untuk nama di chip saringan.
  const [vendorPilih, setVendorPilih] = useState(null);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md). Saringan bawaan
  // "Belum lunas" (tanpa Lunas/Batal) — terlihat sebagai chip, bisa dihapus.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "invoice_date", arah: "desc" },
    saringan: {
      status: saringanStatus(meta, { labelBelum: "Belum lunas" }),
      vendor: { label: "Vendor", param: "vendor_id",
                tampil: (v) => (vendorPilih?.id === v ? vendorPilih.name : "vendor terpilih") },
      dari: saringanDari(),
      sampai: saringanSampai(),
      telat: { label: "Jatuh tempo", param: "overdue_only", tampil: () => "Sudah lewat" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan, nilaiSaringan } = daftar;
  const statusFilter = nilaiSaringan.status;
  const vendorFilter = nilaiSaringan.vendor;
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramStatus(paramSaringan), ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listInvoices(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  // Vendor dari alamat halaman (mis. tautan dibagikan) → nama untuk chip.
  useEffect(() => {
    if (!vendorFilter) { setVendorPilih(null); return; }
    if (vendorPilih?.id === vendorFilter) return;
    ambilVendor(vendorFilter).then(setVendorPilih).catch((e) => {
      setVendorPilih(null); toast.error(apiErrorMessage(e, "Nama vendor saringan gagal dibaca."));
    });
  }, [vendorFilter, vendorPilih?.id]);

  // Dua antrean kerja, masing-masing hanya untuk yang berwenang menanganinya.
  const [pendingApproval, setPendingApproval] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null);

  useEffect(() => {
    getInvoiceMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info status tagihan.")));
    // Vendor TIDAK lagi dimuat seluruhnya — saringan vendor mencari di server (PemilihCari).
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listInvoices(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar tagihan."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  // Angka antrean diambil dari `total` hasil filter BACKEND, bukan dihitung
  // dari halaman yang sedang tampil — kalau dihitung di UI, angkanya hanya
  // benar di halaman 1 lalu diam-diam salah (pola PurchaseOrders).
  const perm = meta?.permissions || {};
  const refreshQueues = useCallback(() => {
    if (perm.approve) {
      listInvoices({ status: "PENDING_APPROVAL", page: 1, page_size: 1 })
        .then(r => setPendingApproval(r.data.total || 0))
        .catch((e) => { setPendingApproval(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    }
    if (perm.reject) {   // reject saat eksekusi = hak FINANCE (tuple backend)
      listInvoices({ status: "UNPAID", page: 1, page_size: 1 })
        .then(r => setPendingPayment(r.data.total || 0))
        .catch((e) => { setPendingPayment(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    }
  }, [perm.approve, perm.reject]);
  // SEKALI per buka layar (begitu hak dari meta terbaca) + tombol Refresh — dulu ikut `items` sehingga
  // terpanggil dua kali saat layar dibuka; DAFTAR-SERAGAM-SISA-KECIL-01 butir 1.
  useEffect(() => { refreshQueues(); }, [refreshQueues]);

  const adaFilter = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "internal_number", judul: "Nomor" },
    { kunci: "invoice_number", judul: "Nomor tagihan vendor" },
    { kunci: "invoice_date", judul: "Tanggal", ambil: (i) => formatDate(i.invoice_date) },
    { kunci: "vendor", judul: "Vendor", ambil: (i) => i.vendor?.name || "" },
    { kunci: "po", judul: "PO", ambil: (i) => i.po?.po_number || "" },
    { kunci: "due_date", judul: "Jatuh tempo", ambil: (i) => (i.due_date ? formatDate(i.due_date) : "Tunai") },
    { kunci: "total_amount", judul: "Nilai" },
    { kunci: "sisa", judul: "Sisa", ambil: (i) => sisaTagihan(i) },
    { kunci: "status", judul: "Status", ambil: (i) => invoiceStatusBadge(meta, i.status).label },
  ];

  const bukaAntrean = (st) => daftar.setSaringan("status", st);

  return (
    <div className="space-y-6" data-testid="inv-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ReceiptText className="h-6 w-6" /> Tagihan Vendor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pengajuan pembayaran ke vendor. Setiap tagihan wajib lewat
            persetujuan Kepala Cabang/Owner sebelum Finance mengeksekusi.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { load(); refreshQueues(); }}
                  data-testid="inv-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {perm.create && (
            <Button size="sm" onClick={() => navigate("/purchasing/invoices/new")}
                    data-testid="inv-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Buat Tagihan
            </Button>
          )}
        </div>
      </div>

      {/* Antrean persetujuan — hanya untuk yang bisa menyetujui. */}
      {perm.approve && pendingApproval > 0 && statusFilter !== "PENDING_APPROVAL" && (
        <button type="button" onClick={() => bukaAntrean("PENDING_APPROVAL")}
                data-testid="inv-approval-banner"
                className="w-full text-left bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3
                           hover:bg-warning-soft/70 transition flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>{pendingApproval} tagihan menunggu persetujuan Anda.</strong>{" "}
            Finance belum bisa membayar sebelum tagihan disetujui.
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      {/* Antrean Finance — hanya untuk yang mengeksekusi pembayaran. */}
      {perm.reject && pendingPayment > 0 && statusFilter !== "UNPAID" && (
        <button type="button" onClick={() => bukaAntrean("UNPAID")}
                data-testid="inv-payment-banner"
                className="w-full text-left bg-info-soft text-info-soft-foreground border border-info/40 rounded p-3
                           hover:bg-info-soft/70 transition flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>{pendingPayment} tagihan sudah disetujui dan menunggu dibayar.</strong>
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor tagihan, vendor, atau nomor PO…" testId="inv-palang"
                    ekspor={{ nama: "tagihan_vendor", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="inv-status-filter" aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          <option value={BELUM_SELESAI}>Belum lunas</option>
          {(meta?.statuses || []).map(s =>
            <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <div className="w-56">
          <PemilihCari value={vendorPilih} bolehKosong
                       onChange={(v) => { setVendorPilih(v); daftar.setSaringan("vendor", v?.id || ""); }}
                       cari={cariVendor} label={labelVendor}
                       placeholder="Semua vendor" placeholderCari="Ketik nama atau kode vendor…"
                       testId="inv-vendor-filter" />
        </div>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          Dari
          <Input type="date" value={nilaiSaringan.dari} data-testid="inv-date-from"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("dari", e.target.value)} />
        </label>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          sampai
          <Input type="date" value={nilaiSaringan.sampai} data-testid="inv-date-to"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("sampai", e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={nilaiSaringan.telat === "true"} data-testid="inv-overdue-filter"
                 onChange={(e) => daftar.setSaringan("telat", e.target.checked ? "true" : "")}
                 className="h-4 w-4 rounded border" />
          Lewat jatuh tempo saja
        </label>
      </PalangDaftar>
      {statusFilter && statusFilter !== BELUM_SELESAI && (
        <p className="text-xs text-muted-foreground -mt-4" data-testid="inv-status-help">
          {invoiceStatusBadge(meta, statusFilter).description}
        </p>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="inv-loading">
            {[...Array(5)].map((_, i) =>
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-2" data-testid="inv-empty">
            {adaFilter ? (
              <p>
                Tidak ada tagihan yang cocok dengan pencarian atau saringan ini.
                Coba hapus saringannya.
              </p>
            ) : (
              <>
                <p>
                  Belum ada tagihan vendor. Tagihan dibuat dari sebuah PO yang
                  sudah terbit — nilainya tidak boleh melebihi nilai PO itu.
                </p>
                {perm.create ? (
                  <Button variant="outline" size="sm" data-testid="inv-empty-create"
                          onClick={() => navigate("/purchasing/invoices/new")}>
                    Buat Tagihan
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" data-testid="inv-empty-goto-po"
                          onClick={() => navigate("/purchasing/po")}>
                    Lihat Purchase Order
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftar} kunci="internal_number" className="px-4 py-3 font-medium">Nomor</ThUrut>
                  <ThUrut daftar={daftar} kunci="invoice_date" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">PO</th>
                  <ThUrut daftar={daftar} kunci="due_date" className="px-4 py-3 font-medium">Jatuh tempo</ThUrut>
                  <ThUrut daftar={daftar} kunci="total_amount" align="right" className="px-4 py-3 font-medium">Nilai</ThUrut>
                  <th className="px-4 py-3 text-right font-medium">Sisa</th>
                  <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                </tr>
              </thead>
              <tbody className="divide-y">
                {daftar.tampil.map(inv => {
                  const b = invoiceStatusBadge(meta, inv.status);
                  const tz = tzForRegion(inv.region_id || inv.region?.id);
                  return (
                    <tr key={inv.id}
                        className={`hover:bg-muted/50 cursor-pointer ${inv.is_overdue ? "bg-danger-soft/40" : ""}`}
                        onClick={() => navigate(`/purchasing/invoices/${inv.id}`)}
                        data-testid={`inv-row-${inv.internal_number}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{inv.internal_number}</div>
                        {inv.invoice_number && (
                          <div className="text-xs text-muted-foreground">
                            Vendor: {inv.invoice_number}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{formatDate(inv.invoice_date, tz)}</td>
                      <td className="px-4 py-3">{inv.vendor?.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {inv.po?.po_number || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {inv.due_date ? (
                          <span className={inv.is_overdue ? "text-danger-soft-foreground font-medium" : ""}>
                            {formatDate(inv.due_date, tz)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Tunai</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{formatIDR(inv.total_amount)}</td>
                      <td className="px-4 py-3 text-right">{formatIDR(sisaTagihan(inv))}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <StatusBadge tone={b.tone} title={b.description}>{b.label}</StatusBadge>
                          {inv.is_overdue && (
                            <StatusBadge tone="danger" testId={`inv-overdue-${inv.internal_number}`}>
                              Lewat jatuh tempo
                            </StatusBadge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="inv-paginasi" />
      </Card>
    </div>
  );
}
